/* eslint-disable @typescript-eslint/no-empty-function */
import { getLocalContext } from '@thoughtspot/blink-context';
import type { Chart } from '@thoughtspot/chart-lib';
import type { ChartDataRenderError } from '@thoughtspot/chart-lib/lib/core/chart-data-validation';
import { ErrorTypes } from '@thoughtspot/chart-lib/lib/core/chart-data-validation';
import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import type { CustomChartLoadingInProgressInfo } from '@thoughtspot/ts-chart-app';
import {
    DownloadExcelTriggerResponse,
    VisualConfig,
} from '@thoughtspot/ts-chart-sdk';
import _ from 'lodash';
import React, { MutableRefObject } from 'react';
import { ControlMenuActionTypes } from 'src/components/axis-menu/axis-menu.util';
import { TABS } from 'src/components/column-editor/table-column-editor/table-column-editor-tab-display';
import {
    AnswerActionDisabledConfig,
    AnswerEditPanelComponents,
    AnswerMode,
    AnswerPageActionsEnum,
    AnswerPageConfigInterface,
    AnswerPageContext,
    CustomActionType,
    DEFAULT_EMPTY_ANSWER_STATE,
    PinboardVizEditContext,
    SearchExecutionState,
    StyleOverides,
    WatchlistItem,
} from '/@services/answer/answer.util';
import { sanitizeExportFileName } from '/@services/answer/answer-download-service';
import {
    AccessMode,
    EmptyAnswer,
    SageSearchResponse,
    TableViz,
} from '/@services/generated/graphql-types';
import { AnswerAction } from '../components/answer/answer-content-header/answer-actions/answer-actions-interfaces';
import { DefinedSizes } from '../components/answer/answer-content-header/answer-actions/menu-button/size-select/size-select-interfaces';
import { ChartSettingsV2MenuItems } from '../components/chart-settings-v2/chart-settings-v2-menu/chart-settings-v2-menu.types';
import { ShowSageTokensConfig } from '../components/conv-assist-answer/conv-assist-answer.util';
import { removeIdFromColumnValueWarningDetailsMap } from '../components/conv-assist-answer/conv-assist-token-with-popover/token-with-popover.container.utils';
import {
    analysisInProgress,
    AnalysisStatus,
} from '../components/spotiq-modal/spotiq-modal.util';
import { getVisibleContextMenuActionItems } from '../components/viz-context-menu/viz-context-menu.util';
import {
    getLayoutPanelState,
    VizEditorTab,
} from '../components/viz-editor/viz-editor.util';
import { AnswerAlertBanner } from '../utils/answer-banner.util';
import { ANSWER_TABLE_START_OFFSET } from './context-contstants';

interface Action {
    type: AnswerPageActionsEnum;
    [key: string]: any;
}

export type VizEditorTabConfig = {
    index: number;
    type: VizEditorTab;
};

type AnswerPageDispatchers = React.Dispatch<Action>;

type DispatchAction = {
    setAnswerSessionId?: (sessionId: string) => void;
    updateAnswerPageConfig?: (
        updateActionState: AnswerPageConfigInterface,
    ) => void;
    updateEmptyPageState?: (emptyAnswerState: EmptyAnswer) => void;
    setPanelState?: (
        answerPanelState: AnswerEditPanelComponents | ChartSettingsV2MenuItems,
    ) => void;
    setAnswerMode?: (answerMode: AnswerMode) => void;
    setAnswerPageContext?: (pageContext: AnswerPageContext) => void;
    updateChart?: (chart: React.MutableRefObject<Chart>) => void;
    updateTable?: (table: TableViz) => void;
    updateSelectedColumnId?: (selectedColumnId: string) => void;
    updateSelectedAxisId?: (selectedAxisId: string) => void;
    setAnswerDescriptionClassname?: (
        answerDescriptionClassname: string,
    ) => void;
    setVizConfigValidationError?: (vizConfigValidationError: string) => void;
    setColumnIdToBeRemoved?: (columnId: string) => void;
    setClipboardToast?: (showToast: boolean) => void;
    setTableEditorTabId?: (tabId: string) => void;
    updatePinboardVizEditContext?: (
        pinboardVizEditContext: PinboardVizEditContext,
    ) => void;
    updateTableDataStartOffset?: (startOffset: number) => void;
    setSelectedMetric?: (selectedMetric: { index: number; type: TABS }) => void;
    setVizEditorTab?: (vizEditorTab: VizEditorTabConfig) => void;
    setFetchFreshData?: (fetchFreshData: boolean) => void;
    setTableLoadingState?: (isTableLoading: boolean) => void;
    setIsLeftPanelCollapsed?: (isLeftPanelCollapsed: boolean) => void;
    updateHiddenActions?: (actions: Set<any>) => void;
    setIsActionMenuHidden?: (isActionMenuHidden: boolean) => void;
    setIsAnswerLoading?: (isAnswerLoading: boolean) => void;
    setShowLoadingIndicatorOnAnswerPage?: (
        showLoadingIndicatorOnAnswerPage: boolean,
    ) => void;
    setIsCustomActionsHidden?: (isCustomActionsHidden: boolean) => void;
    setMenuActions?: (menuActions: Set<AnswerAction>) => void;
    setMenuActionsDisabledConfig?: (
        disabledConfig: Map<Action | string, AnswerActionDisabledConfig>,
    ) => void;
    setButtonActions?: (buttonActions: Set<AnswerAction>) => void;
    setActionsRightToMoreButton?: (
        actionsRightToMoreButton: AnswerAction[],
    ) => void;
    setIsAnswerTitleDisabled?: (disabled: boolean) => void;
    setIsAnswerDescriptionDisabled?: (disabled: boolean) => void;
    setIsAnswerHeadDisabled?: (disabled: boolean) => void;
    setIsEditTitleDisabled?: (disabled: boolean) => void;
    setActionHandlers?: (actionHandlers?: { [key: number]: any }) => void;
    setTileLayoutSize?: (tileLayoutSize: DefinedSizes) => void;
    setShowActions?: (showActions: boolean) => void;
    setIsPresented?: (isPresented: boolean) => void;
    setIsActionBarHistoryEnabled?: (isEnabled: boolean) => void;
    setIsPinboardUnsaved?: (isPinboardUnsaved: boolean) => void;
    setVizColumnConfigChange?: (vizColumnConfigChange: boolean) => void;
    setHighlightAnswerHeader?: (highlightAnswerHeader: boolean) => void;
    setSageQueryState?: (sageQueryState: SageSearchResponse) => void;
    setShowSageTokens?: (showSageTokens: boolean) => void;
    setShowSageTokensConfig?: (
        showSageTokensConfig: ShowSageTokensConfig,
    ) => void;
    setAiAnswerDescription?: (aiAnswerDescription: string) => void;
    updateChartDataRenderError?: (
        chartDataRenderError: ChartDataRenderError,
    ) => void;
    setHeadlinePrefetchError?: (vizId: string) => void;
    setIsQueryVisualizerOpen?: (isQueryVisualizerOpen: boolean) => void;
    setShowAnswerAlertBanner?: (answerAlertBanner: AnswerAlertBanner) => void;
    setIsAnswerHeaderMenuDisabled?: (
        isAnswerHeaderMenuDisabled: boolean,
    ) => void;
    setAnswerHeaderMenuDisabledReason?: (
        answerHeaderMenuDisabledReason: string,
    ) => void;
    setIsSaveViewDisabled?: (isSaveViewDisabled: boolean) => void;
    setIsVersionHistoryVisible?: (isVersionHistoryVisible: boolean) => void;
    setIsVersionHistoryInView?: (isVersionHistoryInView: boolean) => void;
    setShowVersionHistoryToast?: (
        showVersionHistorySuccessToast: boolean,
    ) => void;
    setAnswerId?: (answerId: string) => void;
    setCustomActions?: (customActions: CustomActionType[]) => void;
    setCustomChartVisualConfig?: (
        customChartVisualConfig: VisualConfig,
    ) => void;
    setSpotIQAnalysisStatus?: (spotIQAnalysisStatus: AnalysisStatus) => void;
    setIsSpotIQAlertOpen?: (isSpotIQAlertOpen: boolean) => void;
    setSpotIQAnalysisType?: (analysisType: string) => void;
    setIsKpiFromHomeWatchlist?: (flag: boolean) => void;
    setHasOAuthFailure?: (hasOAuthFailure: boolean) => void;
    setTableWithNoData?: (noData: boolean) => void;
    setSelectedColumnsData?: (selectedColumnsData: Set<string>) => void;
    setAdvancedPivotTableXlsxDownloadCb?: (
        advancedPivotTableXlsxDownloadCb: (
            answerTitle: string,
        ) => Promise<DownloadExcelTriggerResponse>,
    ) => void;
    removeWarningDetails?: (guids: string[]) => void;
    setPreferredRootWarning?: (warning: {
        message: string;
        link: string;
    }) => void;
    setIsChartColumnFormattingUpdated?: (
        isChartColumnFormattingUpdated: boolean,
    ) => void;
    setCustomChartLoadingInProgressInfo?: (
        info: CustomChartLoadingInProgressInfo
    ) => void;
    setIsColumnEditorOpen?: (isColumnEditorOpen: boolean) => void;
    setWatchlistPriorityList?: (watchlistPriorityList: WatchlistItem[]) => void;
    setIsExpandModalOpen?: (isExpandModalOpen: boolean) => void;
    setStyleOverrides?: (styleOverides: StyleOverides) => void;
    setIsHydrated?: (isHydrated: boolean) => void;
    setIsServedFromCache?: (isServedFromCache: boolean) => void;
    setChartElement?: (chartElement: HTMLDivElement) => void;
    setSearchExecutionState?: (
        searchExecutionState: SearchExecutionState,
    ) => void;
};

const defaultAnswerPageActionsState: AnswerPageConfigInterface = {
    answerId: '',
    answerSessionId: '',
    insightSpecId: '',
    isA3Hidden: false,
    isActionMenuHidden: false,
    isSharingHidden: false,
    isCustomRHidden: false,
    isPathAnalyzerHidden: true,
    isVisualizationPinnerHidden: false,
    uplevelSaveBtn: false,
    hideChooseSources: false,
    isInsertInToSlideEnabled: false,
    isChartSwitcherHidden: false,
    isLeftPanelDisabled: false,
    isLeftPanelHidden: false,
    isLeftPanelCollapsed: false,
    isChartSelectorHidden: false,
    isActionSelectorHidden: false,
    isQueryDetailsHidden: false,
    isChartConfiguratorHidden: false,
    isAnswerEditToolbarHidden: false,
    emptyAnswerState: DEFAULT_EMPTY_ANSWER_STATE,
    isSwitchToV1Hidden: false,
    chart: null,
    table: null,
    answerPanelState: null,
    selectedColumnId: null,
    selectedAxisId: null,
    answerDescriptionClassname: '',
    hasAiGeneratedDescription: false,
    isInsightDataSupported: false,
    vizConfigValidationError: '',
    isAdhocAnswer: false,
    answerMode: AnswerMode.ANSWER,
    pageContext: null,
    isSavedView: false,
    cohortColumnId: null,
    columnIdToBeRemoved: null,
    showClipboardToast: false,
    tableEditorTabId: null,
    openShareDialogOnInit: false,
    isTableLoading: true,
    pinboardVizEditContext: {
        disableUpdate: false,
        isPinboardViz: false,
        originalVizParams: null,
        isHeadlineVizRemoved: false,
        onPinboardModelUpdate: (
            _updatePinboardModelId: string,
            containerId?: string,
        ) => { },
    },
    answerPermissions: {
        objectAccessLevel: AccessMode.Modify,
        dataSourceAccessLevel: AccessMode.ReadOnly,
        dataSourceNamesWithNoAccess: [],
    },
    tableBatchSize: envFlags.getValue(FLAGS.dataBatchSize?.name) || 1000,
    tableStartOffset: ANSWER_TABLE_START_OFFSET,
    selectedMetric: { index: null, type: null },
    vizEditorTab: {
        index: 0,
        type: VizEditorTab.CONFIGURE,
    } as VizEditorTabConfig,
    fetchFreshData: false,
    shouldEnableFirstPinExperience: false,
    canShowDualIntentPinButton: false,
    customMenuActions: [],
    isCustomActionsHidden: false,
    hiddenActions: new Set<string>(),
    hiddenAxisMenuActions: new Set<ControlMenuActionTypes>(),
    isSpotqlAnswer: false,
    getCustomColumnMenuItems: undefined,
    visibleActions: null,
    isFilterPanelHidden: false,
    onSaveCB: undefined,
    isAnswerTitleDisabled: false,
    isAnswerDescriptionDisabled: false,
    isAnswerHeadDisabled: false,
    headerClassName: '',
    isEditTitleDisabled: false,
    isAxisContextMenuDisabled: false,
    eurekaSageToggleHidden: false,
    showSearchButtonTour: true,
    isAnswerLoading: false,
    showLoadingIndicatorOnAnswerPage: false,
    isHydrated: true,
    isServedFromCache: false,
    buttonActions: new Set([AnswerAction.INSERT_INTO_SLIDE]),
    menuActions: new Set([
        AnswerAction.SAVE,
        AnswerAction.SAVE_AS_VIEW,
        AnswerAction.EDIT_A_COPY,
        AnswerAction.CREATE_MONITOR,
        AnswerAction.MANAGE_MONITOR,
        AnswerAction.SHOW_UNDERLYING_DATA,
        AnswerAction.DOWNLOAD,
        AnswerAction.TML,
        AnswerAction.EXPORT_TML,
        AnswerAction.UPDATE_TML,
        AnswerAction.EDIT_TML,
        AnswerAction.DELETE,
        AnswerAction.SPOTIQ_ANALYZE,
        AnswerAction.ENABLE_VERSION_CONTROL,
        AnswerAction.SHOW_VERSION_HISTORY,
        AnswerAction.DISABLE_VERSION_CONTROL,
        AnswerAction.MONITOR,
        AnswerAction.SYNC,
        AnswerAction.SWITCH_TO_NEW_CHART_EXPERIENCE,
        AnswerAction.SWITCH_TO_CLASSIC_CHART_EXPERIENCE,
        AnswerAction.ADD_TO_WATCHLIST,
        AnswerAction.REMOVE_FROM_WATCHLIST,
        AnswerAction.PUBLISH,
        AnswerAction.MANAGE_PUBLISHING,
        AnswerAction.UNPUBLISH,
        AnswerAction.REPORT_ISSUE,
    ]),
    showActions: true,
    isPresented: false,
    isActionBarHistoryEnabled: false,
    eagerLoadAnswerTableData: false,
    enableChangeAnalysisContextMenuAction: true,
    isPinboardUnsaved: false,
    vizColumnConfigChange: false,
    getVisibleContextMenuActions: getVisibleContextMenuActionItems,
    highlightAnswerHeader: false,
    showSageTokens: true,
    aiAnswerDescription: '',
    hideSyncActions: false,
    chartDataRenderError: {
        type: ErrorTypes.VALIDATION_PENDING,
    },
    headlinePrefetchErrorMap: {},
    isQueryVisualizerOpen: false,
    exploreContext: {
        onPinningSuccessCallback: (
            pinnedPinboardId: string,
            containerId?: string,
        ) => { },
    },
    changeAnalysisContext: {
        onPinningSuccessCallback: (
            pinnedPinboardId: string,
            containerId?: string,
        ) => { },
    },
    answerAlertBanner: null,
    isAnswerHeaderMenuDisabled: false,
    answerHeaderMenuDisabledReason: '',
    isVersionHistoryVisible: false,
    isVersionHistoryInView: false,
    showVersionHistorySuccessToast: false,
    customActions: [],
    transientAnswerMap: new Map<string, string>(),
    onIterativeChangeAnalysis: null,
    hasSinglePointKPIValue: false,
    showIterativeMenuItem: false,
    spotIQAnalysisStatus: analysisInProgress,
    isSpotIQAlertOpen: false,
    spotIQAnalysisType: '',
    isKpiFromHomeWatchlist: false,
    isChangeAnalysisInView: false,
    hasOAuthFailure: false,
    tableWithNoData: false,
    selectedColumnsData: new Set<string>(),
    customChartVisualConfig: {},
    guidToWarningDetails: new Map(),
    // State for showing preferred root warning on answer page
    // This warning is shown when a query is made and the answer has multiple query join paths
    // which results in ambigous roots, when this occurs a warning needs to be shown that a preferred path was selected
    // also a documentation link is given for the warning which shows how to set the preferred root
    preferredRootWarning: null,
    isChartColumnFormattingUpdated: false,
    customChartLoadingInProgressInfo: { isLoading: false },
    isColumnEditorOpen: false,
    watchlistPriorityList: [],
    // State for showing expand button on answer page
    isExpandModalOpen: false,
    styleOverides: {
        vizActionButtonSize: 'm',
        removeVerticalSpacingBetweenTitleAndDesc: false,
    },
    chartElement: null,
    searchExecutionState: SearchExecutionState.Idle,
};

/**
 * Common AnswerPageContextProvider flags shared by fullscreen modal dialogs
 * (cohort editor, input table builder, etc.) that embed an AnswerContainer
 * but want to suppress all the standard answer chrome.
 */
export const FULLSCREEN_MODAL_ANSWER_PAGE_FLAGS = {
    hideChooseSources: true,
    isAnswerHeadDisabled: true,
    isVisualizationPinnerHidden: true,
    hideSyncActions: true,
    isA3Hidden: true,
    isCustomRHidden: true,
    isActionSelectorHidden: true,
    isActionMenuHidden: true,
    isChartSelectorHidden: true,
    isQueryDetailsHidden: true,
    isChartConfiguratorHidden: true,
    isAnswerEditToolbarHidden: false,
    isSharingHidden: true,
    isChartSwitcherHidden: true,
} as const;

const reducer = (
    state: AnswerPageConfigInterface,
    action: Action,
): AnswerPageConfigInterface => {
    switch (action.type) {
        case AnswerPageActionsEnum.SET_ANSWER_SESSION:
            return {
                ...state,
                answerSessionId: action.sessionId,
            };
        case AnswerPageActionsEnum.SET_INSIGHT_SPEC_ID:
            if (state.insightSpecId === action.insightId) {
                return state;
            }
            return {
                ...state,
                insightSpecId: action.insightId,
            };

        case AnswerPageActionsEnum.SET_ANSWER_ACTIONS:
            return { ...state, ...action.updateActionState };
        case AnswerPageActionsEnum.SET_EMPTY_STATE:
            return { ...state, emptyAnswerState: action.emptyAnswerState };
        case AnswerPageActionsEnum.SET_CHART:
            if (state.chart === action.chart) {
                return state;
            }
            return { ...state, chart: action.chart };
        case AnswerPageActionsEnum.SET_CHART_ELEMENT:
            if (state.chartElement === action.chartElement) {
                return state;
            }
            return { ...state, chartElement: action.chartElement };
        case AnswerPageActionsEnum.SET_TABLE:
            if (state.table === action.table) {
                return state;
            }
            return { ...state, table: action.table };
        case AnswerPageActionsEnum.SET_COLUMN:
            return {
                ...state,
                selectedColumnId: action.selectedColumnId,
                selectedAxisId: null,
            };
        case AnswerPageActionsEnum.SET_AXIS:
            return {
                ...state,
                selectedColumnId: null,
                selectedAxisId: action.selectedAxisId,
            };
        case AnswerPageActionsEnum.SET_ANSWER_PANEL_STATE:
            return { ...state, answerPanelState: action.answerPanelState };
        case AnswerPageActionsEnum.SET_ANSWER_DESCRIPTION_CLASSNAME:
            return {
                ...state,
                answerDescriptionClassname: action.answerDescriptionClassname,
            };
        case AnswerPageActionsEnum.SET_VIZ_CONFIG_VALIDATION_ERROR:
            return {
                ...state,
                vizConfigValidationError: action.vizConfigValidationError,
            };
        case AnswerPageActionsEnum.SET_COLUMN_ID_TO_BE_REMOVED:
            return {
                ...state,
                selectedColumnId: null,
                selectedAxisId: null,
                selectedMetric: { index: null, type: null },
                answerPanelState: getLayoutPanelState(),
                columnIdToBeRemoved: action.columnIdToBeRemoved,
            };
        case AnswerPageActionsEnum.SET_VIZ_COLUMN_CONFIG_CHANGE:
            if (state.vizColumnConfigChange === action.vizColumnConfigChange)
                return state;
            return {
                ...state,
                vizColumnConfigChange: action.vizColumnConfigChange,
            };
        case AnswerPageActionsEnum.SET_CLIPBOARD_TOAST:
            return { ...state, showClipboardToast: action.showClipboardToast };
        case AnswerPageActionsEnum.SET_TABLE_EDITOR_TAB_ID:
            return { ...state, tableEditorTabId: action.tableEditorTabId };
        case AnswerPageActionsEnum.SET_PINBOARD_VIZ_EDIT_CONTEXT:
            return {
                ...state,
                pinboardVizEditContext: action.pinboardVizEditContext,
            };
        case AnswerPageActionsEnum.SET_ANSWER_MODE:
            if (state.answerMode === action.answerMode) return state;
            return {
                ...state,
                answerMode: action.answerMode,
            };
        case AnswerPageActionsEnum.SET_ANSWER_PAGE_CONTEXT:
            if (state.pageContext === action.pageContext) return state;
            return {
                ...state,
                pageContext: action.pageContext,
            };
        case AnswerPageActionsEnum.SET_TABLEDATA_START_OFFSET:
            return {
                ...state,
                tableStartOffset: action.startOffset,
            };
        case AnswerPageActionsEnum.SET_SELECTED_METRIC:
            return {
                ...state,
                selectedMetric: { ...action.selectedMetric },
            };
        case AnswerPageActionsEnum.SET_VIZ_EDITOR_TAB:
            return {
                ...state,
                vizEditorTab: { ...action.vizEditorTab },
            };
        case AnswerPageActionsEnum.SET_FETCH_FRESH_DATA:
            return {
                ...state,
                fetchFreshData: action.fetchFreshData,
            };
        case AnswerPageActionsEnum.SET_TABLE_LOADING:
            if (state.isTableLoading === action.isTableLoading) return state;
            return {
                ...state,
                isTableLoading: action.isTableLoading,
            };
        case AnswerPageActionsEnum.SET_LEFT_PANEL_COLLAPSED_STATE:
            if (state.isLeftPanelCollapsed === action.isLeftPanelCollapsed)
                return state;
            return {
                ...state,
                isLeftPanelCollapsed: action.isLeftPanelCollapsed,
            };
        case AnswerPageActionsEnum.SET_HIDDEN_MENU_ACTIONS:
            return {
                ...state,
                hiddenActions: action.hiddenActions,
            };
        case AnswerPageActionsEnum.SET_IS_ACTION_MENU_HIDDEN:
            if (state.isActionMenuHidden === action.isActionMenuHidden)
                return state;
            return {
                ...state,
                isActionMenuHidden: action.isActionMenuHidden,
            };
        case AnswerPageActionsEnum.SET_IS_ANSWER_LOADING:
            if (state.isAnswerLoading === action.isAnswerLoading) return state;
            return {
                ...state,
                isAnswerLoading: action.isAnswerLoading,
            };
        case AnswerPageActionsEnum.SET_SHOW_LOADING_INDICATOR_ON_ANSWER_PAGE:
            if (
                state.showLoadingIndicatorOnAnswerPage ===
                action.showLoadingIndicatorOnAnswerPage
            )
                return state;
            return {
                ...state,
                showLoadingIndicatorOnAnswerPage:
                    action.showLoadingIndicatorOnAnswerPage,
            };
        case AnswerPageActionsEnum.SET_IS_HYDRATED:
            if (state.isHydrated === action.isHydrated) return state;
            return {
                ...state,
                isHydrated: action.isHydrated,
            };
        case AnswerPageActionsEnum.SET_IS_SERVED_FROM_CACHE:
            if (state.isServedFromCache === action.isServedFromCache) return state;
            return {
                ...state,
                isServedFromCache: action.isServedFromCache,
            };
        case AnswerPageActionsEnum.SET_IS_CUSTOM_ACTIONS_HIDDEN:
            if (state.isCustomActionsHidden === action.isCustomActionsHidden)
                return state;
            return {
                ...state,
                isCustomActionsHidden: action.isCustomActionsHidden,
            };
        case AnswerPageActionsEnum.SET_MENU_ACTIONS:
            return {
                ...state,
                menuActions: action.menuActions,
            };
        case AnswerPageActionsEnum.SET_MENU_ACTIONS_DISABLED_CONFIG:
            if (
                _.isEqual(
                    state.menuActionsDisabledConfig,
                    action.disabledConfig,
                )
            ) {
                return state;
            }
            return {
                ...state,
                menuActionsDisabledConfig: new Map(action.disabledConfig),
            };

        case AnswerPageActionsEnum.SET_SHOW_VERSION_HISTORY_SUCCESS_TOAST:
            if (
                state.showVersionHistorySuccessToast ===
                action.showVersionHistorySuccessToast
            )
                return state;
            return {
                ...state,
                showVersionHistorySuccessToast:
                    action.showVersionHistorySuccessToast,
            };

        case AnswerPageActionsEnum.SET_BUTTON_ACTIONS:
            return {
                ...state,
                buttonActions: action.buttonActions,
            };
        case AnswerPageActionsEnum.SET_ACTIONS_RIGHT_TO_MORE_BUTTON:
            return {
                ...state,
                actionsRightToMoreButton: action.actionsRightToMoreButton,
            };
        case AnswerPageActionsEnum.SET_ANSWER_TITLE_DISABLED:
            if (state.isAnswerTitleDisabled === action.disabled) return state;
            return {
                ...state,
                isAnswerTitleDisabled: action.disabled,
            };
        case AnswerPageActionsEnum.SET_ANSWER_DESCRIPTION_DISABLED:
            if (state.isAnswerDescriptionDisabled === action.disabled)
                return state;
            return {
                ...state,
                isAnswerDescriptionDisabled: action.disabled,
            };
        case AnswerPageActionsEnum.SET_ANSWER_HEAD_DISABLED:
            if (state.isAnswerHeadDisabled === action.disabled) return state;
            return {
                ...state,
                isAnswerHeadDisabled: action.disabled,
            };
        case AnswerPageActionsEnum.SET_EDIT_TITLE_DISABLED:
            if (state.isEditTitleDisabled === action.disabled) return state;
            return {
                ...state,
                isEditTitleDisabled: action.disabled,
            };
        case AnswerPageActionsEnum.SET_TILE_LAYOUT_SIZE:
            if (state.tileLayoutSize === action.tileLayoutSize) return state;
            return {
                ...state,
                tileLayoutSize: action.tileLayoutSize,
            };
        case AnswerPageActionsEnum.SET_ACTION_HANDLERS:
            return {
                ...state,
                actionHandlers: action.actionHandlers,
            };
        case AnswerPageActionsEnum.SET_SHOW_ACTIONS:
            if (state.showActions === action.showActions) return state;
            return {
                ...state,
                showActions: action.showActions,
            };
        case AnswerPageActionsEnum.SET_IS_PRESENTED:
            if (state.isPresented === action.isPresented) return state;
            return {
                ...state,
                isPresented: action.isPresented,
            };
        case AnswerPageActionsEnum.SET_ACTION_BAR_HISTORY_ENABLED:
            if (state.isActionBarHistoryEnabled === action.isEnabled)
                return state;
            return {
                ...state,
                isActionBarHistoryEnabled: action.isEnabled,
            };
        case AnswerPageActionsEnum.SET_IS_PINBOARD_UNSAVED:
            return {
                ...state,
                isPinboardUnsaved: action.isPinboardUnsaved,
            };
        case AnswerPageActionsEnum.SET_HIGHLIGHT_ANSWER_HEADER:
            if (state.highlightAnswerHeader === action.highlightAnswerHeader)
                return state;
            return {
                ...state,
                highlightAnswerHeader: action.highlightAnswerHeader,
            };
        case AnswerPageActionsEnum.SET_SAGE_QUERY_STATE:
            if (state.sageQueryState === action.sageQueryState) return state;
            return {
                ...state,
                sageQueryState: action.sageQueryState,
            };
        case AnswerPageActionsEnum.SHOW_SAGE_TOKENS:
            if (state.showSageTokens === action.showSageTokens) return state;
            return {
                ...state,
                showSageTokens: action.showSageTokens,
            };
        case AnswerPageActionsEnum.SHOW_SAGE_TOKENS_CONFIG:
            return {
                ...state,
                showSageTokensConfig: action.showSageTokensConfig,
            };
        case AnswerPageActionsEnum.AI_ANSWER_REQUEST_ID:
            if (state.aiAnswerRequestId === action.aiAnswerRequestId)
                return state;
            return {
                ...state,
                aiAnswerRequestId: action.aiAnswerRequestId,
            };
        case AnswerPageActionsEnum.SET_AI_ANSWER_DESCRIPTION:
            if (state.aiAnswerDescription === action.aiAnswerDescription)
                return state;
            return {
                ...state,
                aiAnswerDescription: action.aiAnswerDescription,
            };
        case AnswerPageActionsEnum.SET_CHART_DATA_RENDER_ERROR:
            if (state.chartDataRenderError === action.chartDataRenderError)
                return state;
            return {
                ...state,
                chartDataRenderError: action.chartDataRenderError,
            };
        case AnswerPageActionsEnum.SET_HEADLINE_PREFETCH_ERROR:
            if (state.headlinePrefetchErrorMap[action.vizId]) return state;
            return {
                ...state,
                headlinePrefetchErrorMap: {
                    ...state.headlinePrefetchErrorMap,
                    [action.vizId]: true,
                },
            };
        case AnswerPageActionsEnum.SET_IS_QUERY_VISUALIZER_OPEN:
            if (state.isQueryVisualizerOpen === action.isQueryVisualizerOpen)
                return state;
            return {
                ...state,
                isQueryVisualizerOpen: action.isQueryVisualizerOpen,
            };
        case AnswerPageActionsEnum.SET_ANSWER_ALERT_BANNER:
            if (
                state.answerAlertBanner?.message ===
                action.answerAlertBanner?.message
            )
                return state;
            return {
                ...state,
                answerAlertBanner: action.answerAlertBanner,
            };
        case AnswerPageActionsEnum.SET_IS_ANSWER_HEADER_MENU_DISABLED:
            if (
                state.isAnswerHeaderMenuDisabled ===
                action.isAnswerHeaderMenuDisabled
            )
                return state;
            return {
                ...state,
                isAnswerHeaderMenuDisabled: action.isAnswerHeaderMenuDisabled,
            };
        case AnswerPageActionsEnum.SET_ANSWER_HEADER_MENU_DISABLED_REASON:
            if (
                state.answerHeaderMenuDisabledReason ===
                action.answerHeaderMenuDisabledReason
            )
                return state;
            return {
                ...state,
                answerHeaderMenuDisabledReason:
                    action.answerHeaderMenuDisabledReason,
            };
        case AnswerPageActionsEnum.SET_IS_VERSION_HISTORY_VISIBLE:
            if (
                state.isVersionHistoryVisible === action.isVersionHistoryVisible
            )
                return state;
            return {
                ...state,
                isVersionHistoryVisible: action.isVersionHistoryVisible,
            };
        case AnswerPageActionsEnum.SET_IS_VERSION_HISTORY_INVIEW:
            if (state.isVersionHistoryInView === action.isVersionHistoryInView)
                return state;
            return {
                ...state,
                isVersionHistoryInView: action.isVersionHistoryInView,
            };
        case AnswerPageActionsEnum.SET_IS_SAVE_VIEW_DISABLED:
            if (state.isSaveViewDisabled === action.isSaveViewDisabled)
                return state;
            return {
                ...state,
                isSaveViewDisabled: action.isSaveViewDisabled,
            };
        case AnswerPageActionsEnum.SET_ANSWER_ID:
            if (state.answerId === action.answerId) return state;
            return {
                ...state,
                answerId: action.answerId,
            };
        case AnswerPageActionsEnum.SET_CUSTOM_ACTIONS:
            if (state.customActions === action.customActions) return state;
            return {
                ...state,
                customActions: action.customActions,
            };

        case AnswerPageActionsEnum.SET_SPOTIQ_ANALYSIS_STATUS:
            return {
                ...state,
                spotIQAnalysisStatus: action.spotIQAnalysisStatus,
            };
        case AnswerPageActionsEnum.SET_IS_SPOTIQ_ALERT_OPEN:
            return {
                ...state,
                isSpotIQAlertOpen: action.isSpotIQAlertOpen,
            };
        case AnswerPageActionsEnum.SET_SPOTIQ_ANALYSIS_TYPE:
            return {
                ...state,
                spotIQAnalysisType: action.analysisType,
            };
        case AnswerPageActionsEnum.SET_IS_KPI_FROM_HOME_WATCHLIST:
            return {
                ...state,
                isKpiFromHomeWatchlist: action.isKpiFromHomeWatchlist,
            };
        case AnswerPageActionsEnum.SET_HAS_OAUTH_FAILURE:
            // Bail out when unchanged: a new state object would re-notify
            // every tracked consumer for a no-op update.
            if (state.hasOAuthFailure === action.hasOAuthFailure) {
                return state;
            }
            return {
                ...state,
                hasOAuthFailure: action.hasOAuthFailure,
            };
        case AnswerPageActionsEnum.SET_TABLE_WITH_NO_DATA:
            return {
                ...state,
                tableWithNoData: action.noData,
            };
        case AnswerPageActionsEnum.SET_SELECTED_COLUMNS_DATA:
            return {
                ...state,
                selectedColumnsData: action.selectedColumnsData,
            };
        case AnswerPageActionsEnum.SET_CUSTOM_CHART_VISUAL_CONFIG:
            return {
                ...state,
                customChartVisualConfig: action.customChartVisualConfig,
            };
        case AnswerPageActionsEnum.SET_ADVANCED_PIVOT_TABLE_XLSX_DOWNLOAD_CB:
            return {
                ...state,
                advancedPivotTableXlsxDownloadCb:
                    action.advancedPivotTableXlsxDownloadCb,
            };
        case AnswerPageActionsEnum.REMOVE_WARNING_DETAILS_BY_GUIDS:
            return {
                ...state,
                guidToWarningDetails: removeIdFromColumnValueWarningDetailsMap(
                    state.guidToWarningDetails,
                    action.guids,
                ),
            };
        case AnswerPageActionsEnum.SET_PREFERRED_ROOT_WARNINGS:
            return {
                ...state,
                preferredRootWarning: action.warning,
            };
        case AnswerPageActionsEnum.SET_IS_CHART_COLUMN_FORMATTING_UPDATED:
            return {
                ...state,
                isChartColumnFormattingUpdated:
                    action.isChartColumnFormattingUpdated,
            };
        case AnswerPageActionsEnum.SET_CUSTOM_CHART_LOADING_IN_PROGRESS_INFO:
            return {
                ...state,
                customChartLoadingInProgressInfo:
                    action.customChartLoadingInProgressInfo,
            };

        case AnswerPageActionsEnum.SET_IS_COLUMN_EDITOR_OPEN:
            return {
                ...state,
                isColumnEditorOpen: action.isColumnEditorOpen,
            };
        case AnswerPageActionsEnum.SET_WATCHLIST_PRIORITY_LIST:
            return {
                ...state,
                watchlistPriorityList: action.watchlistPriorityList,
            };
        case AnswerPageActionsEnum.SET_IS_EXPAND_MODAL_OPEN:
            return {
                ...state,
                isExpandModalOpen: action.isExpandModalOpen,
            };
        case AnswerPageActionsEnum.SET_STYLE_OVERRIDES:
            return {
                ...state,
                styleOverides: action.styleOverides,
            };
        case AnswerPageActionsEnum.SET_SEARCH_EXECUTION_STATE:
            if (state.searchExecutionState === action.searchExecutionState) {
                return state;
            }
            return {
                ...state,
                searchExecutionState: action.searchExecutionState,
            };

        default:
            return state;
    }
};

const getDispatcherFunctions = (
    dispatch: AnswerPageDispatchers,
): DispatchAction => {
    const setAnswerSessionId = (sessionId: string) => {
        dispatch({ type: AnswerPageActionsEnum.SET_ANSWER_SESSION, sessionId });
    };
    const setInsightSpecId = (insightId: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_INSIGHT_SPEC_ID,
            insightId,
        });
    };
    const updateAnswerPageConfig = (
        updateActionState: AnswerPageConfigInterface,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_ACTIONS,
            updateActionState,
        });
    };
    const updateEmptyPageState = (emptyAnswerState: EmptyAnswer) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_EMPTY_STATE,
            emptyAnswerState,
        });
    };
    const setPanelState = (answerPanelState: AnswerEditPanelComponents) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_PANEL_STATE,
            answerPanelState,
        });
    };
    const setAnswerMode = (answerMode: AnswerMode) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_MODE,
            answerMode,
        });
    };
    const setAnswerPageContext = (pageContext: AnswerPageContext) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_PAGE_CONTEXT,
            pageContext,
        });
    };
    const updateChart = (chart: MutableRefObject<Chart>) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_CHART,
            chart,
        });
    };
    const updateTable = (table: TableViz) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_TABLE,
            table,
        });
    };
    const updateSelectedColumnId = (selectedColumnId: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_COLUMN,
            selectedColumnId,
        });
    };
    const updateSelectedAxisId = (selectedAxisId: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_AXIS,
            selectedAxisId,
        });
    };
    const setAnswerDescriptionClassname = (
        answerDescriptionClassname: string,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_DESCRIPTION_CLASSNAME,
            answerDescriptionClassname,
        });
    };
    const setVizConfigValidationError = (vizConfigValidationError: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_VIZ_CONFIG_VALIDATION_ERROR,
            vizConfigValidationError,
        });
    };
    const setColumnIdToBeRemoved = (columnId: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_COLUMN_ID_TO_BE_REMOVED,
            columnIdToBeRemoved: columnId,
        });
    };
    const setClipboardToast = (showToast: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_CLIPBOARD_TOAST,
            showClipboardToast: showToast,
        });
    };
    const setTableEditorTabId = (tabId: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_TABLE_EDITOR_TAB_ID,
            tableEditorTabId: tabId,
        });
    };
    const updatePinboardVizEditContext = (
        pinboardVizEditContext: PinboardVizEditContext,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_PINBOARD_VIZ_EDIT_CONTEXT,
            pinboardVizEditContext,
        });
    };

    const updateTableDataStartOffset = (startOffset: number) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_TABLEDATA_START_OFFSET,
            startOffset,
        });
    };

    const setSelectedMetric = (selectedMetric: {
        index: number;
        type: TABS;
    }) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SELECTED_METRIC,
            selectedMetric,
        });
    };
    const setVizEditorTab = (vizEditorTab: VizEditorTabConfig) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_VIZ_EDITOR_TAB,
            vizEditorTab,
        });
    };

    const setFetchFreshData = (fetchFreshData: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_FETCH_FRESH_DATA,
            fetchFreshData,
        });
    };

    const setTableLoadingState = (isTableLoading: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_TABLE_LOADING,
            isTableLoading,
        });
    };

    const setIsLeftPanelCollapsed = (isLeftPanelCollapsed: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_LEFT_PANEL_COLLAPSED_STATE,
            isLeftPanelCollapsed,
        });
    };

    const updateHiddenActions = (actions: Set<any>) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_HIDDEN_MENU_ACTIONS,
            hiddenActions: actions,
        });
    };

    const setIsActionMenuHidden = (isActionMenuHidden: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_ACTION_MENU_HIDDEN,
            isActionMenuHidden,
        });
    };

    const setIsAnswerLoading = (isAnswerLoading: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_ANSWER_LOADING,
            isAnswerLoading,
        });
    };

    const setShowLoadingIndicatorOnAnswerPage = (
        showLoadingIndicatorOnAnswerPage: boolean,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SHOW_LOADING_INDICATOR_ON_ANSWER_PAGE,
            showLoadingIndicatorOnAnswerPage,
        });
    };

    const setIsHydrated = (isHydrated: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_HYDRATED,
            isHydrated,
        });
    };

    const setIsServedFromCache = (isServedFromCache: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_SERVED_FROM_CACHE,
            isServedFromCache,
        });
    };

    const setIsCustomActionsHidden = (isCustomActionsHidden: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_CUSTOM_ACTIONS_HIDDEN,
            isCustomActionsHidden,
        });
    };

    const setMenuActions = (menuActions: Set<AnswerAction>) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_MENU_ACTIONS,
            menuActions,
        });
    };

    const setMenuActionsDisabledConfig = (
        disabledConfig: Map<Action | string, AnswerActionDisabledConfig>,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_MENU_ACTIONS_DISABLED_CONFIG,
            disabledConfig,
        });
    };

    const setButtonActions = (buttonActions: Set<AnswerAction>) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_BUTTON_ACTIONS,
            buttonActions,
        });
    };

    const setShowVersionHistoryToast = (
        showVersionHistorySuccessToast: boolean,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SHOW_VERSION_HISTORY_SUCCESS_TOAST,
            showVersionHistorySuccessToast,
        });
    };

    const setActionsRightToMoreButton = (
        actionsRightToMoreButton: AnswerAction[],
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ACTIONS_RIGHT_TO_MORE_BUTTON,
            actionsRightToMoreButton,
        });
    };

    const setIsAnswerTitleDisabled = (disabled: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_TITLE_DISABLED,
            disabled,
        });
    };
    const setIsAnswerDescriptionDisabled = (disabled: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_DESCRIPTION_DISABLED,
            disabled,
        });
    };
    const setIsAnswerHeadDisabled = (disabled: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_HEAD_DISABLED,
            disabled,
        });
    };
    const setIsEditTitleDisabled = (disabled: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_EDIT_TITLE_DISABLED,
            disabled,
        });
    };
    const setActionHandlers = (actionHandlers?: { [key: number]: any }) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ACTION_HANDLERS,
            actionHandlers,
        });
    };

    const setTileLayoutSize = (tileLayoutSize: DefinedSizes) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_TILE_LAYOUT_SIZE,
            tileLayoutSize,
        });
    };

    const setShowActions = (showActions: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SHOW_ACTIONS,
            showActions,
        });
    };

    const setIsPresented = (isPresented: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_PRESENTED,
            isPresented,
        });
    };

    const setIsActionBarHistoryEnabled = (isEnabled: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ACTION_BAR_HISTORY_ENABLED,
            isEnabled,
        });
    };

    const setIsPinboardUnsaved = (isPinboardUnsaved: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_PINBOARD_UNSAVED,
            isPinboardUnsaved,
        });
    };

    const setVizColumnConfigChange = (vizColumnConfigChange: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_VIZ_COLUMN_CONFIG_CHANGE,
            vizColumnConfigChange,
        });
    };

    const setHighlightAnswerHeader = (highlightAnswerHeader: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_HIGHLIGHT_ANSWER_HEADER,
            highlightAnswerHeader,
        });
    };

    const setSageQueryState = (sageQueryState: SageSearchResponse) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SAGE_QUERY_STATE,
            sageQueryState,
        });
    };

    const setShowSageTokens = (showSageTokens: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SHOW_SAGE_TOKENS,
            showSageTokens,
        });
    };

    const setShowSageTokensConfig = (
        showSageTokensConfig: ShowSageTokensConfig,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SHOW_SAGE_TOKENS_CONFIG,
            showSageTokensConfig,
        });
    };

    const setAiAnswerDescription = (aiAnswerDescription: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_AI_ANSWER_DESCRIPTION,
            aiAnswerDescription,
        });
    };

    const updateChartDataRenderError = (
        chartDataRenderError: ChartDataRenderError,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_CHART_DATA_RENDER_ERROR,
            chartDataRenderError,
        });
    };

    const setHeadlinePrefetchError = (vizId: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_HEADLINE_PREFETCH_ERROR,
            vizId,
        });
    };

    const setIsQueryVisualizerOpen = (isQueryVisualizerOpen: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_QUERY_VISUALIZER_OPEN,
            isQueryVisualizerOpen,
        });
    };
    const setShowAnswerAlertBanner = (answerAlertBanner: AnswerAlertBanner) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_ALERT_BANNER,
            answerAlertBanner,
        });
    };
    const setIsAnswerHeaderMenuDisabled = (
        isAnswerHeaderMenuDisabled: boolean,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_ANSWER_HEADER_MENU_DISABLED,
            isAnswerHeaderMenuDisabled,
        });
    };

    const setAnswerHeaderMenuDisabledReason = (
        answerHeaderMenuDisabledReason: string,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_HEADER_MENU_DISABLED_REASON,
            answerHeaderMenuDisabledReason,
        });
    };

    const setIsVersionHistoryVisible = (isVersionHistoryVisible: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_VERSION_HISTORY_VISIBLE,
            isVersionHistoryVisible,
        });
    };

    const setIsVersionHistoryInView = (isVersionHistoryInView: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_VERSION_HISTORY_INVIEW,
            isVersionHistoryInView,
        });
    };

    const setIsSaveViewDisabled = (isSaveViewDisabled: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_SAVE_VIEW_DISABLED,
            isSaveViewDisabled,
        });
    };

    const setAnswerId = (answerId: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_ANSWER_ID,
            answerId,
        });
    };

    const setCustomActions = (customActions: CustomActionType[]) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_CUSTOM_ACTIONS,
            customActions,
        });
    };

    const setSpotIQAnalysisStatus = (spotIQAnalysisStatus: AnalysisStatus) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SPOTIQ_ANALYSIS_STATUS,
            spotIQAnalysisStatus,
        });
    };

    const setIsSpotIQAlertOpen = (isSpotIQAlertOpen: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_SPOTIQ_ALERT_OPEN,
            isSpotIQAlertOpen,
        });
    };

    const setSpotIQAnalysisType = (analysisType: string) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SPOTIQ_ANALYSIS_TYPE,
            analysisType,
        });
    };

    const setIsKpiFromHomeWatchlist = (isKpiFromHomeWatchlist: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_KPI_FROM_HOME_WATCHLIST,
            isKpiFromHomeWatchlist,
        });
    };

    const setHasOAuthFailure = (hasOAuthFailure: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_HAS_OAUTH_FAILURE,
            hasOAuthFailure,
        });
    };

    const setTableWithNoData = (noData: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_TABLE_WITH_NO_DATA,
            noData,
        });
    };
    const setCustomChartVisualConfig = (
        customChartVisualConfig: VisualConfig,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_CUSTOM_CHART_VISUAL_CONFIG,
            customChartVisualConfig,
        });
    };

    const setSelectedColumnsData = (selectedColumnsData: Set<string>) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SELECTED_COLUMNS_DATA,
            selectedColumnsData,
        });
    };

    const setAdvancedPivotTableXlsxDownloadCb = (
        advancedPivotTableXlsxDownloadCb: (answerTitle: string) => Promise<any>,
    ) => {
        dispatch({
            type:
                AnswerPageActionsEnum.SET_ADVANCED_PIVOT_TABLE_XLSX_DOWNLOAD_CB,
            advancedPivotTableXlsxDownloadCb: (answerTitle: string) =>
                advancedPivotTableXlsxDownloadCb(
                    sanitizeExportFileName(answerTitle),
                ),
        });
    };

    const removeWarningDetails = (guids: string[]) => {
        dispatch({
            type: AnswerPageActionsEnum.REMOVE_WARNING_DETAILS_BY_GUIDS,
            guids,
        });
    };

    const setPreferredRootWarning = (warning: {
        message: string;
        link: string;
    }) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_PREFERRED_ROOT_WARNINGS,
            warning,
        });
    };

    const setIsChartColumnFormattingUpdated = (
        isChartColumnFormattingUpdated: boolean,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_CHART_COLUMN_FORMATTING_UPDATED,
            isChartColumnFormattingUpdated,
        });
    };

    const setCustomChartLoadingInProgressInfo = (
        customChartLoadingInProgressInfo: CustomChartLoadingInProgressInfo
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_CUSTOM_CHART_LOADING_IN_PROGRESS_INFO,
            customChartLoadingInProgressInfo
        });
    };

    const setIsColumnEditorOpen = (isColumnEditorOpen: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_COLUMN_EDITOR_OPEN,
            isColumnEditorOpen,
        });
    };

    const setWatchlistPriorityList = (
        watchlistPriorityList: WatchlistItem[],
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_WATCHLIST_PRIORITY_LIST,
            watchlistPriorityList,
        });
    };

    const setIsExpandModalOpen = (isExpandModalOpen: boolean) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_IS_EXPAND_MODAL_OPEN,
            isExpandModalOpen,
        });
    };

    const setStyleOverrides = (styleOverides: StyleOverides) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_STYLE_OVERRIDES,
            styleOverides,
        });
    };

    const setSearchExecutionState = (
        searchExecutionState: SearchExecutionState,
    ) => {
        dispatch({
            type: AnswerPageActionsEnum.SET_SEARCH_EXECUTION_STATE,
            searchExecutionState,
        });
    };

    return {
        updateAnswerPageConfig,
        updateEmptyPageState,
        updateChart,
        updateTable,
        updateSelectedColumnId,
        updateSelectedAxisId,
        setPanelState,
        setAnswerMode,
        setAnswerPageContext,
        setAnswerDescriptionClassname,
        setVizConfigValidationError,
        setColumnIdToBeRemoved,
        setClipboardToast,
        setTableEditorTabId,
        updatePinboardVizEditContext,
        updateTableDataStartOffset,
        setSelectedMetric,
        setVizEditorTab,
        setFetchFreshData,
        setAnswerSessionId,
        setTableLoadingState,
        setIsLeftPanelCollapsed,
        updateHiddenActions,
        setIsActionMenuHidden,
        setIsAnswerLoading,
        setShowLoadingIndicatorOnAnswerPage,
        setIsHydrated,
        setIsServedFromCache,
        setIsCustomActionsHidden,
        setMenuActions,
        setMenuActionsDisabledConfig,
        setButtonActions,
        setActionsRightToMoreButton,
        setIsAnswerTitleDisabled,
        setIsAnswerDescriptionDisabled,
        setIsAnswerHeadDisabled,
        setIsEditTitleDisabled,
        setTileLayoutSize,
        setActionHandlers,
        setShowActions,
        setIsPresented,
        setIsActionBarHistoryEnabled,
        setIsPinboardUnsaved,
        setVizColumnConfigChange,
        setHighlightAnswerHeader,
        setSageQueryState,
        setShowSageTokens,
        setShowSageTokensConfig,
        setAiAnswerDescription,
        updateChartDataRenderError,
        setHeadlinePrefetchError,
        setIsQueryVisualizerOpen,
        setShowAnswerAlertBanner,
        setIsAnswerHeaderMenuDisabled,
        setAnswerHeaderMenuDisabledReason,
        setIsSaveViewDisabled,
        setIsVersionHistoryVisible,
        setIsVersionHistoryInView,
        setShowVersionHistoryToast,
        setAnswerId,
        setCustomActions,
        setSpotIQAnalysisStatus,
        setIsSpotIQAlertOpen,
        setSpotIQAnalysisType,
        setIsKpiFromHomeWatchlist,
        setHasOAuthFailure,
        setTableWithNoData,
        setSelectedColumnsData,
        setCustomChartVisualConfig,
        setAdvancedPivotTableXlsxDownloadCb,
        removeWarningDetails,
        setPreferredRootWarning,
        setIsChartColumnFormattingUpdated,
        setCustomChartLoadingInProgressInfo,
        setIsColumnEditorOpen,
        setWatchlistPriorityList,
        setIsExpandModalOpen,
        setStyleOverrides,
        setSearchExecutionState,
    };
};

const {
    TrackedContextProvider: AnswerPageContextProvider,
    getTrackedState: getAnswerPageConfig,
    getTrackedMethods: setAnswerPageConfig,
} = getLocalContext<AnswerPageConfigInterface, DispatchAction>(
    'answer-context',
    reducer,
    defaultAnswerPageActionsState,
    getDispatcherFunctions,
);

export { AnswerPageContextProvider, getAnswerPageConfig, setAnswerPageConfig };
